import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";
import ApiService from "../../service/ApiService";
import { apiPaths } from "../../service/apiPaths";
import { GridColDef, GridFilterModel, GridSortModel } from "@mui/x-data-grid";

const useCouponCategory = (columns: GridColDef[]) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [couponCategoryData, setCouponCategoryData] = useState<any[]>([]);
  const [selectedCouponCategoryId, setSelectedCouponCategoryId] = useState<string | null>(null);
  const [selectedCouponCategoryName, setSelectedCouponCategoryName] = useState<string>("");
  const [filterModel, setFilterModel] = useState<GridFilterModel>();
  const [sortingModel, setSortingModel] = useState<GridSortModel>();
  // New state for dedicated time sorting
  const [timeSortOrder, setTimeSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // DataGrid is 0-indexed
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0); // Total number of rows

  const navigate = useNavigate();
  const { id } = useParams();

  // Debounced search effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!id) {
        fetchCouponCategories();
      }
    }, 500); // 500ms debounce delay

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  useEffect(() => {
    if (id) {
      setSelectedCouponCategoryId(id);
      // Optionally fetch the name if needed
      // fetchCouponCategoryName(id);
    } else {
      fetchCouponCategories();
    }
  }, [id, paginationModel, filterModel, sortingModel, timeSortOrder]); // Added timeSortOrder to dependencies

  // Effect to reset time sort when grid sort is used
  useEffect(() => {
    if (sortingModel && sortingModel.length > 0) {
      setTimeSortOrder(null);
    }
  }, [sortingModel]);

  const fetchCouponCategories = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: filterModel?.items.map(({ field, operator, value }) => ({
          field,
          type: columns?.find((col) => col.field === field)?.type || "string",
          operator,
          value,
        })),
        // Prioritize timeSortOrder, otherwise use the grid's sortingModel
        sortBy: timeSortOrder ? "createdAt" : sortingModel?.[0]?.field || "",
        sortOrder: timeSortOrder || sortingModel?.[0]?.sort || undefined,
      };

      // Add search query parameter if provided
      if (searchQuery && searchQuery.trim().length > 0) {
        params.q = searchQuery.trim();
      }

      const response = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.coupons}/categories`,
        params
      });
      console.log("CouponCategory : ", response);

      if (response?.data) {
        setCouponCategoryData(response?.data || []);
        setRowCount(response?.totalCount || 0);
      } else if (response?.categories) {
        setCouponCategoryData(response.categories || []);
        setRowCount(response.categories?.length || 0);
      } else {
        setCouponCategoryData(response || []);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching coupon categories:", error);
      toast.error("Failed to load coupon categories");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: string, name: string, description: string) => {

    navigate(`/edit-coupon-category/${id}`, {
      state: {
        name, description
      }
    });
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    try {
      await ApiService({
        method: "DELETE",
        endpoint: `${apiPaths.deleteCategory}/${id}`
      })
      toast.success("Coupon  deleted successfully");
      fetchCouponCategories();
    } catch (error) {
      console.error("Error deleting coupon :", error);
      toast.error("Failed to delete coupon ");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate("/add-coupon-category");
  };

  // These are included to match the interface of the original hook
  // but might not be needed for coupon categories
  const handleStatusUp = () => { };
  const handleDateFilter = () => { };

  // New handler to cycle through time sorting options
  const handleSortByTime = () => {
    // When using this button, clear the grid's own sort model
    setSortingModel([]);
    setTimeSortOrder((prev) => {
      if (prev === "desc") return "asc";
      if (prev === "asc") return null; // Turn off
      return "desc"; // Start with descending
    });
  };

  return {
    selectedCouponCategoryId,
    selectedCouponCategoryName,
    couponCategoryData,
    loading,
    searchQuery,
    setSearchQuery,
    handleEdit,
    handleDelete,
    handleStatusUp,
    handleDateFilter,
    handleAdd,
    setFilterModel,
    setSortingModel,
    rowCount,
    paginationModel,
    setPaginationModel,
    // Export the new handler and sort order
    handleSortByTime,
    timeSortOrder,
  };
};

export default useCouponCategory;
